import React, { useState, useEffect } from 'react'
import {
  CAccordion,
  CAccordionBody,
  CAccordionHeader,
  CAccordionItem,
  CButton,
  CFormCheck,
  CModal,
  CModalBody,
  CModalHeader,
} from '@coreui/react-pro'
import './DiagnosisCard.scss'
import '../../views/pages/census/components/ConfirmationModals/ConfirmModal.scss'
import './PdpmAiModal.scss'
import { postAiAssist } from '../../apiHelper/aiAssistantApi'
import { GET_DX_SUGGESTIONS } from '../../apiHelper/apiRoute/aiAssistant'
import { addDxDot } from '../common/shared/sharedDXLogic'
import { getDiagnosisByCodes } from '../../helper/backendHelper'

async function getPDPMSuggestions(dxCodes, dxSuggestions) {
  return postAiAssist(GET_DX_SUGGESTIONS, {
    diagnosis_codes: dxCodes,
  })
    .then((res) => {
      if (res?.dx_code_optimizations) {
        const newDxSuggestions = { ...dxSuggestions }
        res.dx_code_optimizations.forEach((dx) => {
          newDxSuggestions[dx.original_code] = {
            codes: dx.optimized_diagnoses,
            alreadyRecommended: false,
          }
        })
        return newDxSuggestions
      }
      return {}
    })
    .catch((e) => {
      console.error(e)
      return {}
    })
}

export default function PdpmAiModal({ dxCodes = [], setSelectedDiagnosis }) {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [dxSuggestions, setDxSuggestions] = useState({})
  const [openOption, setOpenOption] = useState(-1)
  const [dxUpdates, setDxUpdates] = useState({})
  const [isLoading, setIsLoading] = useState(false)

  const onCloseModal = () => {
    if (isLoading) {
      return
    }
    setIsLoading(true)

    const dxToSwap =
      Object.values(dxUpdates)
        ?.flat()
        ?.map((dx) => dx.replaceAll('.', '')) || []

    if (dxToSwap.length > 0) {
      getDiagnosisByCodes(dxToSwap)
        .then((res) => {
          if (res.results?.length > 0) {
            const newDxList = []
            dxCodes.forEach((oldDx) => {
              const betterDx = res.results.find(
                (newDx) =>
                  newDx.code.replaceAll('.', '') === dxUpdates[oldDx.code]?.replaceAll('.', ''),
              )
              if (betterDx) {
                // save as no suggestion, since it was an already recommended code
                dxSuggestions[betterDx.code] = { codes: [], alreadyRecommended: true }
              }

              newDxList.push(betterDx || oldDx)
            })
            setSelectedDiagnosis(newDxList)
            setDxUpdates({})
          }
        })
        .catch((e) => console.error(e))
        .finally(() => {
          setOpenOption(-1)
          setIsModalOpen(false)
          setIsLoading(false)
        })
    } else {
      setOpenOption(-1)
      setIsModalOpen(false)
      setIsLoading(false)
    }
  }

  useEffect(() => {
    const newDx = dxCodes.filter((dx) => !dxSuggestions[dx.code]).map((dx) => dx.code)
    if (isModalOpen && newDx.length > 0) {
      setIsLoading(true)
      getPDPMSuggestions(newDx, dxSuggestions)
        .then((newSuggs) => {
          setDxSuggestions(newSuggs)
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }, [isModalOpen, dxCodes])

  return (
    <>
      <CButton
        className={`${dxCodes?.length > 0 ? 'ai' : ''}`}
        color="secondary"
        size="sm"
        variant="outline"
        shape="rounded-pill"
        style={{ padding: '4px 16px' }}
        onClick={() => {
          setIsModalOpen(true)
        }}
        disabled={dxCodes?.length === 0}
      >
        Optimize for PDPM
      </CButton>

      <CModal
        className="confirm-modal pdmp-modal"
        visible={isModalOpen}
        onClose={onCloseModal}
        fullscreen="lg"
        alignment={'center'}
      >
        <CModalHeader className="d-flex">
          Optimize for PDPM
          {isLoading ? (
            <i className="fa-duotone fa-solid fa-spinner-third fa-spin ms-4 pdpm-refresh"></i>
          ) : (
            <i
              className={'fa-sharp fa-light fa-arrows-rotate pdpm-refresh'}
              onClick={async () => {
                setIsLoading(true)
                await getPDPMSuggestions(
                  dxCodes.map((dx) => dx.code),
                  dxSuggestions,
                )
                setIsLoading(false)
              }}
            ></i>
          )}
        </CModalHeader>
        <CModalBody>
          <CAccordion alwaysOpen={false}>
            {dxCodes?.map((currentDxCode, index) => (
              <CAccordionItem itemKey={index} key={currentDxCode.code}>
                <CAccordionHeader
                  onClick={() => {
                    setOpenOption((prev) => {
                      if (prev === index) {
                        return -1
                      }
                      return index
                    })
                  }}
                >
                  <div className="dxsuggestion-title-holder">
                    <div>
                      {addDxDot(currentDxCode.code)}: {currentDxCode.description}
                    </div>
                    {dxUpdates[currentDxCode.code] && (
                      <div
                        className="btn btn-secondary"
                        onClick={(e) => {
                          e.stopPropagation()
                          const newDxUpdates = { ...dxUpdates }
                          delete newDxUpdates[currentDxCode.code]
                          setDxUpdates(newDxUpdates)
                        }}
                      >
                        Clear
                      </div>
                    )}

                    {!dxSuggestions[currentDxCode.code]?.alreadyRecommended && (
                      <i
                        className={`chevron-icon fa-sharp fa-solid fa-chevron-${
                          openOption === index ? 'up' : 'down'
                        }`}
                      ></i>
                    )}
                  </div>
                </CAccordionHeader>
                <CAccordionBody>
                  <div className="radio-group-hold">
                    {(!dxSuggestions[currentDxCode.code] ||
                      dxSuggestions[currentDxCode.code]?.alreadyRecommended ||
                      dxSuggestions[currentDxCode.code]?.codes?.length === 0) && (
                      <div>No additional recommendations</div>
                    )}
                    {dxSuggestions[currentDxCode.code]?.codes?.map((option) => (
                      <div key={currentDxCode.code + '-' + option?.code} className="radio-holder">
                        <CFormCheck
                          type="radio"
                          name={currentDxCode.code + 'options'}
                          id={option.code}
                          label={option?.code + ': ' + option?.description}
                          onChange={(e) => {
                            if (e.currentTarget.checked) {
                              setDxUpdates((prev) => ({
                                ...prev,
                                [currentDxCode.code]: option.code,
                              }))
                            }
                          }}
                          checked={dxUpdates[currentDxCode.code] === option.code}
                        />
                      </div>
                    ))}
                  </div>
                </CAccordionBody>
              </CAccordionItem>
            ))}
          </CAccordion>
        </CModalBody>
      </CModal>
    </>
  )
}
